import { clone } from '../../../webmodule-common/other/clone';
import { compareFrames, isFrameCompareReportEmpty } from '../../../webmodule-common/interop/interfaces/frame-data';
import { customElement, state } from 'lit/decorators.js';
import { EventTemplate } from '../../../webmodule-common/other/ui/events';
import { FabricationJobContainerManager } from '../data/fabrication-job-container';
import { FabricationJobItem, InputUnitOverride } from '../../api/fabrication-job-api';
import { fireQuickErrorToast } from '../../../webmodule-common/other/toast-away';
import { FranchiseeQuoteItemFrameViewForV6 } from '../../dealer-franchisee/quotes/views/v6/franchisee-v6-quote-item-frame-view';
import { getApiFactory } from '../../api/api-injector';
import { getV6QuoteItemForContainer, virtualPathToUrl } from '../../quotes/data/v6/helper-functions';
import { secureFetch } from '../../api/secure-fetch';
import { html, nothing, TemplateResult } from 'lit';
import { LangFabricationJobItemFrameView } from '../../../localization/scripts/fabrication-jobs/views/fabrication-job-item-frame-view-lang';
import { QuoteContainerManager } from '../../quotes/data/quote-container';
import { QuoteItemContainer } from '../../quotes/data/quote-item-container';
import { QuoteItemViewOptions } from '../../quotes/views/quote-item-view';
import { saveWithIndicator } from '../../../webmodule-common/other/save-workflow';
import { toJsonStr } from '../../../webmodule-common/other/blob-converters';
import { reloadFromDataProviderSupport, v6Util } from '../../../webmodule-common/interop/interfaces/v6-config';

export interface FabricationJobItemFrameViewOptions {
  quoteItemViewOptions: QuoteItemViewOptions;
  fabricationJobItem: FabricationJobItem;
  fabricationJobManager: FabricationJobContainerManager;
  quoteManager: QuoteContainerManager;
  quoteItemContainer: QuoteItemContainer;
  supplierId: string;
}

/**
 * Extends the standard V6 frame view to provide per-unit option editing
 * via a tab bar. Tab 0 ("Quoted") shows original options readonly.
 * Tabs 1..N ("Unit 1".."Unit N") show editable copies for site adjustments.
 *
 * Uses a single V6 editor instance (created by the parent) and swaps data
 * by changing what the `quoteItem` getter returns, then refreshing the editor.
 * The V6 editor reads from the QuoteFrameDataProvider dynamically on each render.
 *
 * Saving integrates with the standard DataEntryView save framework:
 * prepareForSave → internalDataChanged → internalSaveData → save indicator.
 */
@customElement('wm-fabrication-job-item-frame-view')
export class FabricationJobItemFrameView extends FranchiseeQuoteItemFrameViewForV6 {
  private fabOptions: FabricationJobItemFrameViewOptions;

  /** 0 = Quoted (readonly), 1..N = Unit 1..N (editable) */
  @state() private activeTabIndex = 0;

  private unitCount = 1;

  /**
   * Per-unit V6QuoteItem snapshots (outer V6QuoteItemExternalStorage wrappers).
   * Index 0 = original quoted data (deep-cloned, never modified — used as the
   * readonly "Quoted" tab and as the baseline for change detection).
   * Index 1..N = editable unit copies, initially cloned from the original or
   * loaded from previously saved overrides.
   */
  private unitSnapshots: (object | null)[] = [];

  /** Tracks which unit tabs have been modified by the user. */
  @state() private unitModified: boolean[] = [];

  /** Tracks which unit tabs are still loading their blob data. */
  @state() private unitLoading: boolean[] = [];

  /** Tracks which unit tabs failed to load their override blob. */
  @state() private unitFetchFailed: boolean[] = [];

  /** The original V6QuoteItem from the quote item container. */
  private originalV6QuoteItem: object | undefined;

  /** Bound handler for editor change detection. */
  private editorChangeHandler = this.onEditorInteraction.bind(this);
  private editorChangeTimeout: ReturnType<typeof setTimeout> | null = null;
  private saving = false;

  constructor(options: FabricationJobItemFrameViewOptions) {
    super(options.quoteItemViewOptions);
    this.fabOptions = options;
  }

  public async afterConstruction(): Promise<void> {
    // Initialize unit data before the parent creates the V6 editor
    this.originalV6QuoteItem = getV6QuoteItemForContainer(this.fabOptions.quoteItemContainer);
    // Per-unit override editing requires V6QuoteFrameEditor.reloadFromDataProvider,
    // which only exists in v6config v2.2.4+. On older versions fall back to a
    // read-only "Quoted" view (no Unit 1..N tabs, no fetches, no saves).
    const supportsUnitOverrides = reloadFromDataProviderSupport();
    this.unitCount = supportsUnitOverrides ? (this.fabOptions.quoteItemContainer.item.quantity ?? 1) : 0;

    const existingOverrides = supportsUnitOverrides ? (this.fabOptions.fabricationJobItem.unitOverrides ?? []) : [];

    // Index 0 = Quoted tab. Must be a deep clone so V6 editor mutations on unit
    // tabs don't bleed back into the original reference.
    this.unitSnapshots = [this.originalV6QuoteItem ? clone(this.originalV6QuoteItem) : null];
    this.unitModified = [false];
    this.unitLoading = [false];
    this.unitFetchFailed = [false];

    // Index 1..N = initialize all units with clones so the editor can render
    // immediately. Units with saved overrides are marked as loading — their
    // blob data will be fetched in the background after the component renders.
    const unitsToFetch: { index: number; virtualPath: string }[] = [];
    for (let i = 1; i <= this.unitCount; i++) {
      const existing = existingOverrides.find(o => o.unitIndex === i - 1);
      const hasOverride = !!existing?.v6QuoteItemVirtualPath;
      this.unitSnapshots.push(this.originalV6QuoteItem ? clone(this.originalV6QuoteItem) : null);
      this.unitModified.push(hasOverride);
      this.unitLoading.push(hasOverride);
      this.unitFetchFailed.push(false);
      if (hasOverride) {
        unitsToFetch.push({ index: i, virtualPath: existing!.v6QuoteItemVirtualPath! });
      }
    }

    // Start on the Quoted tab (readonly)
    this.activeTabIndex = 0;

    // Let the parent create the V6 editor and render the component first
    await super.afterConstruction();

    // Now fetch blob data for all units with overrides in parallel
    if (unitsToFetch.length > 0) {
      this.fetchUnitOverridesInBackground(unitsToFetch);
    }
  }

  /**
   * Fetches unit override blobs in parallel in the background. As each blob
   * arrives, the unit's snapshot is swapped in and the loading flag is cleared.
   * If the user is currently viewing a tab whose data just loaded, the editor
   * is refreshed to show the fetched data.
   *
   * Concurrency note: these fetches write to unitSnapshots[] concurrently with
   * the tab-switch save path (onTabClick → internalSaveData). This is safe because:
   * 1. JS is single-threaded — the save loop (internalSaveData lines 317–324) reads
   *    all snapshots synchronously with no await, so no fetch callback can interleave.
   * 2. The unitLoading guard in onTabClick prevents saving the active tab while its
   *    fetch is still in progress.
   * 3. The worst case is a fetch completing during an await in the save path for a
   *    *different* tab — this causes a no-op re-save of already-persisted data,
   *    which the backend's delta merge handles idempotently.
   */
  private fetchUnitOverridesInBackground(units: { index: number; virtualPath: string }[]): void {
    for (const unit of units) {
      this.fetchSingleUnitOverride(unit.index, unit.virtualPath);
    }
  }

  private async fetchSingleUnitOverride(unitIndex: number, virtualPath: string): Promise<void> {
    // Defensive: per-unit override editing requires v6config v2.2.4+. With unitCount = 0
    // this path is unreachable, but the guard protects against future refactors.
    if (!reloadFromDataProviderSupport()) return;
    let fetchOk = false;
    try {
      const url = virtualPathToUrl(virtualPath);
      const response = await secureFetch(url);
      if (response.ok) {
        const json = await response.text();
        this.unitSnapshots[unitIndex] = JSON.parse(json);
        fetchOk = true;
      }
    } catch {
      // Cloned original stays in place for display, but we prevent save from overwriting
    }

    if (!fetchOk) {
      // Mark fetch as failed and clear modified flag to prevent accidental overwrite
      // of the real override with original data during save
      console.warn(
        `[FabricationJobItemFrameView] Failed to fetch unit override for unit ${unitIndex} from ${virtualPath}`
      );
      const modified = [...this.unitModified];
      modified[unitIndex] = false;
      this.unitModified = modified;

      const failed = [...this.unitFetchFailed];
      failed[unitIndex] = true;
      this.unitFetchFailed = failed;
    }

    // Clear loading state (triggers re-render to remove spinner from tab)
    const loading = [...this.unitLoading];
    loading[unitIndex] = false;
    this.unitLoading = loading;

    // If the user is viewing this tab, reload the editor with the fetched data
    if (this.activeTabIndex === unitIndex && this.v6Editor) {
      this.v6Editor.reloadFromDataProvider(this.unitSnapshots[unitIndex]);
      this.v6Editor.resetEditorState();
      this.v6Editor.requestUpdate();
    }
  }

  public override async prepareEditor(): Promise<void> {
    await super.prepareEditor();

    // Listen for interactions on the editor to detect changes early
    if (this.v6Editor?.ui) {
      this.v6Editor.ui.addEventListener('click', this.editorChangeHandler, true);
      this.v6Editor.ui.addEventListener('input', this.editorChangeHandler, true);
      this.v6Editor.ui.addEventListener('change', this.editorChangeHandler, true);
    }
  }

  override disconnectedCallback(): void {
    super.disconnectedCallback();
    if (this.v6Editor?.ui) {
      this.v6Editor.ui.removeEventListener('click', this.editorChangeHandler, true);
      this.v6Editor.ui.removeEventListener('input', this.editorChangeHandler, true);
      this.v6Editor.ui.removeEventListener('change', this.editorChangeHandler, true);
    }
    if (this.editorChangeTimeout) {
      clearTimeout(this.editorChangeTimeout);
      this.editorChangeTimeout = null;
    }
  }

  private onEditorInteraction(): void {
    if (this.activeTabIndex === 0 || !this.v6Editor) return;
    if (this.editorChangeTimeout) clearTimeout(this.editorChangeTimeout);
    this.editorChangeTimeout = setTimeout(() => {
      if (!this.v6Editor) return;
      const isChanged = this.v6Editor.changed;
      if (isChanged !== this.unitModified[this.activeTabIndex]) {
        const modified = [...this.unitModified];
        modified[this.activeTabIndex] = isChanged;
        this.unitModified = modified;
      }
    }, 300);
  }

  // --- QuoteFrameDataProvider overrides ---

  override get quoteItem(): object | undefined {
    return (this.unitSnapshots[this.activeTabIndex] ?? this.originalV6QuoteItem) as object | undefined;
  }

  override get readonly(): boolean {
    if (this.activeTabIndex === 0) return true;
    if (this.unitLoading[this.activeTabIndex]) return true;
    if (this.unitFetchFailed[this.activeTabIndex]) return true;
    return this.fabOptions.fabricationJobManager.isReadOnly();
  }

  /**
   * Quoted tab shows total quantity. Unit tabs show 1 since each unit is a single item.
   */
  override get quantity(): number {
    if (this.activeTabIndex === 0) return this.unitCount;
    return 1;
  }

  /**
   * Override properties template to show quantity as 1 (readonly) for unit tabs.
   * The parent's propertiesTemplate reads quantity from the dataTracker bound to
   * the quote item's total quantity, which is incorrect for individual units.
   */
  override propertiesTemplate(): TemplateResult {
    if (this.activeTabIndex === 0) return super.propertiesTemplate();

    // For unit tabs, render a simplified properties section with quantity = 1
    const item = this.fabOptions.quoteItemContainer.item;
    return html`
      <div class="form-one-col quoteItemFrameProperties">
        <div class="row attributeContents">
          <div class="v6config-group">
            <div class="v6config-group-header">${LangFabricationJobItemFrameView.propertiesHeader()}</div>
            <div class="row mb-1">
              <label class="col-sm-4 col-form-label">${LangFabricationJobItemFrameView.locationLabel()}</label>
              <div class="col-sm-8"><input class="form-control" readonly .value=${item.title ?? ''} /></div>
            </div>
            <div class="row mb-1">
              <label class="col-sm-4 col-form-label">${LangFabricationJobItemFrameView.descriptionLabel()}</label>
              <div class="col-sm-8"><input class="form-control" readonly .value=${item.description ?? ''} /></div>
            </div>
            <div class="row mb-1">
              <label class="col-sm-4 col-form-label">${LangFabricationJobItemFrameView.quantityLabel()}</label>
              <div class="col-sm-8"><input class="form-control" readonly .value=${'1'} /></div>
            </div>
          </div>
        </div>
      </div>
    `;
  }

  /**
   * Override validation to skip the parent's quantity DOM lookup on unit tabs.
   * The parent reads quantity from a dataTracker-bound input element that doesn't
   * exist in our simplified unit tab properties template.
   */
  protected override getValidationErrors(): string[] {
    if (this.activeTabIndex > 0) return []; // No validation needed for unit overrides
    return super.getValidationErrors();
  }

  // --- Save framework overrides ---

  /**
   * Override isDataReadonly for the save framework. The parent checks
   * quoteManager.isReadonly() which is true (forceLockout), but we need
   * the save framework to run prepareForSave/internalSaveData for unit overrides.
   * Readonly is determined by job state, not quote state.
   */
  override isDataReadonly(): boolean {
    if (this.activeTabIndex === 0) return true;
    if (this.unitLoading[this.activeTabIndex]) return true;
    if (this.unitFetchFailed[this.activeTabIndex]) return true;
    return this.fabOptions.fabricationJobManager.isReadOnly();
  }

  /**
   * Capture the current V6 editor state into the active unit's snapshot.
   * Does NOT call super — we don't want the standard quote item save path.
   */
  override async prepareForSave(): Promise<void> {
    await this.captureCurrentEditorState();
  }

  /**
   * Returns true if any unit has unsaved modifications.
   */
  override internalDataChanged(): boolean {
    // Any unit has been modified (captured during tab switch or prepareForSave)
    return this.unitModified.some((m, i) => i > 0 && m);
  }

  /**
   * Save unit overrides to the fabrication API.
   * Only modified units are included in the payload. The fabrication job's
   * recordVersion is sent for optimistic concurrency protection.
   */
  override async internalSaveData(): Promise<boolean> {
    try {
      const overrides: InputUnitOverride[] = [];
      for (let i = 1; i <= this.unitCount; i++) {
        if (this.unitModified[i] && this.unitSnapshots[i] && !this.unitLoading[i] && !this.unitFetchFailed[i]) {
          overrides.push({
            unitIndex: i - 1,
            v6QuoteItemJson: toJsonStr(this.unitSnapshots[i]) ?? null
          });
        }
      }

      const api = getApiFactory().fabricationJob();
      const result = await api.updateFabricationJobItemUnitOverrides({
        fabricationJobItemId: this.fabOptions.fabricationJobItem.id,
        fabricationJobRecordVersion: this.fabOptions.fabricationJobManager.fabricationJob.recordVersion,
        unitOverrides: overrides
      });

      if (result) {
        // Reload the full job + items from the server so the container has fresh data.
        // This ensures the items table's overrides column reflects the saved changes
        // when the user navigates back to the Job Items tab.
        await this.fabOptions.fabricationJobManager.loadFromServer();
        return true;
      }
      return false;
    } catch (e) {
      console.error('Failed to save unit overrides', e);
      fireQuickErrorToast(LangFabricationJobItemFrameView.saveUnitOverridesFailed());
      return false;
    }
  }

  /**
   * Use the simple save indicator (spinning icon), not the full progress modal.
   * Our save is a single API call, not a multi-step process like quote item saves.
   */
  override displaySaveModal(): boolean {
    return false;
  }

  // --- UI ---

  protected override bodyTemplate(): EventTemplate {
    return html`
      ${this.renderTabBar()}
      <div class="fab-unit-panel-header">
        ${this.activeTabIndex === 0
          ? LangFabricationJobItemFrameView.quotedMeasurementsHeader()
          : `${LangFabricationJobItemFrameView.onSiteMeasurementsHeader()} #${this.activeTabIndex}`}
      </div>
      <div class="fab-unit-body">${super.bodyTemplate()}</div>
    `;
  }

  private renderTabBar(): EventTemplate {
    // No tab bar when per-unit overrides are unsupported by the active v6config —
    // there is only one logical tab (Quoted) and the per-unit header handles it.
    if (!reloadFromDataProviderSupport()) return html``;

    const tabs: { label: string; index: number }[] = [{ label: LangFabricationJobItemFrameView.quotedTab(), index: 0 }];
    for (let i = 1; i <= this.unitCount; i++) {
      tabs.push({ label: `#${i}`, index: i });
    }

    return html`
      <div class="fab-unit-panel">
        <ul class="nav nav-tabs fab-unit-tabs">
          ${tabs.map(tab => {
            const isActive = this.activeTabIndex === tab.index;
            const isQuoted = tab.index === 0;
            const isModified = tab.index > 0 && this.unitModified[tab.index];
            const isLoading = tab.index > 0 && this.unitLoading[tab.index];
            const isFetchFailed = tab.index > 0 && this.unitFetchFailed[tab.index];
            const linkClass = `nav-link fab-unit-tab-link ${isQuoted ? 'fab-unit-tab-quoted' : ''} ${isActive ? 'active' : ''}`;
            return html`
              <li class="nav-item">
                <a class="${linkClass}" href="javascript:void(0)" @click=${() => this.onTabClick(tab.index)}>
                  <span class="fab-unit-tab-content"
                    >${tab.label}${isLoading
                      ? html` <i class="fa-solid fa-spinner fa-spin fab-unit-tab-loading-icon"></i>`
                      : isFetchFailed
                        ? html` <i class="fa-solid fa-triangle-exclamation fab-unit-tab-error-icon"></i>`
                        : isModified
                          ? html` <i class="fa-solid fa-pen fab-unit-tab-modified-icon"></i>`
                          : nothing}</span
                  >
                </a>
              </li>
            `;
          })}
        </ul>
      </div>
    `;
  }

  // --- Tab switching ---

  private async onTabClick(tabIndex: number): Promise<void> {
    if (tabIndex === this.activeTabIndex) return;
    if (!this.v6Editor) return;
    if (this.saving) return;
    // Defensive: per-unit override editing requires v6config v2.2.4+. With the tab
    // bar hidden on older versions this is unreachable, but guard against refactors.
    if (!reloadFromDataProviderSupport()) return;

    // Capture and save directly when leaving an editable unit tab.
    // We bypass performAutoSave() because it goes through the resource lock system
    // which is already held by the parent PageControl tab — causing "Already Locked" errors.
    // The framework overrides (prepareForSave/internalSaveData) still handle saves
    // when the PageControl tab itself is closed via allowPageSwitch().
    if (
      this.activeTabIndex > 0 &&
      !this.unitLoading[this.activeTabIndex] &&
      !this.unitFetchFailed[this.activeTabIndex] &&
      !this.fabOptions.fabricationJobManager.isReadOnly()
    ) {
      const captured = await this.captureCurrentEditorState();
      if (!captured) return; // Capture failed — stay on current tab so user can retry
      if (this.unitModified.some((m, i) => i > 0 && m)) {
        this.saving = true;
        try {
          const saved = await saveWithIndicator(async () => await this.internalSaveData());
          if (!saved) return; // Save failed, stay on tab
        } finally {
          this.saving = false;
        }
      }
    }

    // Switch tab — the quoteItem getter now returns the new unit's snapshot
    this.activeTabIndex = tabIndex;

    // Swap the V6 editor's data to match the new tab. reloadFromDataProvider
    // extracts the inner V6QuoteItem from the outer storage wrapper, updates
    // the editor service, preserves the thumbnail, and refreshes the view.
    this.v6Editor.reloadFromDataProvider(this.unitSnapshots[tabIndex]);

    // Reset modification tracking since we loaded different data
    this.v6Editor.resetEditorState();

    // Trigger re-render
    this.v6Editor.requestUpdate();
    this.requestUpdate();
  }

  /**
   * Captures the current V6 editor state by committing the editor, comparing
   * the result against the original quoted data using compareFrames, and
   * marking the active unit as modified if meaningful differences exist.
   */
  private async captureCurrentEditorState(): Promise<boolean> {
    if (!this.v6Editor || this.activeTabIndex === 0) return true;

    try {
      const commitResult = await this.v6Editor.commit();
      if (commitResult?.quoteItem) {
        this.unitSnapshots[this.activeTabIndex] = commitResult.quoteItem as object;

        // Compare the unit's frame data with the original to determine if there
        // are real changes. Uses compareFrames (the same utility the parent class
        // uses for frame diff reporting) instead of raw JSON comparison.
        const unitFrameData = commitResult.quoteItem ? v6Util().getFrameData(commitResult.quoteItem as object) : null;
        const originalFrameData = this.unitSnapshots[0] ? v6Util().getFrameData(this.unitSnapshots[0] as object) : null;
        const diff = compareFrames(originalFrameData, unitFrameData, { excludeHidden: true });
        const isChanged = !isFrameCompareReportEmpty(diff);

        const modified = [...this.unitModified];
        modified[this.activeTabIndex] = isChanged;
        this.unitModified = modified;
      }
      return true;
    } catch (e) {
      console.error('Failed to capture editor state', e);
      fireQuickErrorToast(LangFabricationJobItemFrameView.captureEditorStateFailed());

      // Prevent stale unitModified from triggering a save with old snapshot data
      const modified = [...this.unitModified];
      modified[this.activeTabIndex] = false;
      this.unitModified = modified;

      return false;
    }
  }
}
